import { File06, Lock01 } from '@untitled-ui/icons-react/build/cjs';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import LogoSmall from 'src/assets/img/logo/logo-icon.svg';
import BadgeCmp from 'src/components/BadgeCmp';
import CardCmp from 'src/components/CardCmp';
import NotFoundUI from 'src/components/NotFoundUI';
import TruncateTextCmp from 'src/components/TruncateText';
import { ROUTES } from 'src/constants/routes';
import { appearance } from 'src/redux/basic.slice';
import { orderDetailsData, orderDetailsLoading, selectCurrentLegData, selectOrderLegs } from 'src/redux/orders.slice';
import { getDateWithSuffixFormat, getFileExtensionTypeImage, getFormattedDate, getShortName, onError } from 'src/utils/CommonFunctions';

import { SHARED_DOCUMENTS_CONTENT_CLASS, SharedDocumentsList } from '../../ShareRadarOrder/sections/SharedDocumentsSection';

import FilterDropdown, { IFilterOption } from './FilterDropdown';
import DocumentPreviewModal from './modals/DocumentPreviewModal';

// Documents uploaded/modified within 24 hours are considered "new"
const NEW_DOC_THRESHOLD_MS = 24 * 60 * 60 * 1000;

const DUMMY_DOCS = [
    { id: 1, fileName: 'Bill_of_Lading_WAL-TC3B-1909.pdf', createdAt: '2026-05-18T10:00:00Z', uploadedByType: 'system' },
    { id: 2, fileName: 'Customer_Invoice_WAL-TC3B.pdf', createdAt: '2026-05-20T08:30:00Z', uploadedByType: 'system' },
    { id: 3, fileName: 'Proof_of_Delivery_signed.pdf', createdAt: '2026-05-21T14:15:00Z', uploadedByType: 'user' },
    { id: 3, fileName: 'Proof_of_Delivery_signed.pdf', createdAt: '2026-05-21T14:15:00Z', uploadedByType: 'user' },
];

interface IDocumentsWidgetProps {
    isReadOnly?: boolean;
}

const DocumentsWidget = ({ isReadOnly = false }: IDocumentsWidgetProps) => {
    const { t } = useTranslation();
    const order = useSelector(orderDetailsData);
    const loading = useSelector(orderDetailsLoading);
    const orderLegs = useSelector(selectOrderLegs);
    const currentLegData = useSelector(selectCurrentLegData);
    const orgAppearance = useSelector(appearance);

    const [selectedLeg, setSelectedLeg] = useState<string>('');
    const [isWhiteLogo, setIsWhiteLogo] = useState(false);
    const [previewDoc, setPreviewDoc] = useState<any>(null);

    const documentNameRefs = useRef<Record<string, HTMLParagraphElement | null>>({});
    const uploadedByNameRefs = useRef<Record<string, HTMLParagraphElement | null>>({});

    // TEMPORARY: org logo from the appearance table is disabled (matches useOrgBranding) —
    // always null so the static black WAL icon fallback renders. To re-enable, restore:
    // orgAppearance?.logo && orgAppearance?.logoUrl ? `${orgAppearance.logoUrl}${orgAppearance.logo}` : null
    const orgLogoUrl = useMemo<string | null>(() => null, [orgAppearance]);

    // Check if org logo is mostly white (needs dark background)
    useEffect(() => {
        if (!orgLogoUrl) return;

        const img = new Image();
        img.crossOrigin = 'anonymous';

        img.onload = () => {
            try {
                const canvas = document.createElement('canvas');
                canvas.width = img.naturalWidth;
                canvas.height = img.naturalHeight;
                const ctx = canvas.getContext('2d');

                if (!ctx) return;
                ctx.drawImage(img, 0, 0);

                const { data } = ctx.getImageData(0, 0, canvas.width, canvas.height);
                let totalBrightness = 0;
                let pixelCount = 0;

                for (let i = 0; i < data.length; i += 4) {
                    const alpha = data[i + 3];

                    if (alpha > 10) {
                        totalBrightness += (data[i] + data[i + 1] + data[i + 2]) / 3;
                        pixelCount++;
                    }
                }

                setIsWhiteLogo(pixelCount > 0 && totalBrightness / pixelCount > 200);
            } catch {
                setIsWhiteLogo(false);
            }
        };
        img.onerror = () => setIsWhiteLogo(false);
        img.src = orgLogoUrl;
    }, [orgLogoUrl]);

    // Leg filter options: "All Legs" + an "Order" bucket for order-level documents (the
    // master BOL etc., orderLegId NULL) + each leg from Redux. The master tab entry
    // (value `isMaster-...`) is not a leg and owns no leg documents — skip it.
    const legOptions = useMemo((): IFilterOption[] => {
        if (!orderLegs || orderLegs.length === 0) return [];

        return [
            { value: '', label: 'All Legs' },
            ...(Array.isArray(order?.orderDocuments) && order.orderDocuments.length > 0 ? [{ value: 'order', label: `Order (#${order?.orderId})` }] : []),
            ...orderLegs.filter((leg: any) => !`${leg.value}`.includes('isMaster-')).map((leg: any) => ({ value: leg.value, label: leg.name })),
        ];
    }, [orderLegs, order?.orderDocuments, order?.orderId]);

    // Flatten all leg documents, injecting legId + legShowId onto each doc.
    // For the active leg, use currentLegData.documents so upload/delete reflects immediately
    // without waiting for a full order re-fetch (those actions only update currentLegData in Redux).
    const allDocs = useMemo(() => {
        const parseDocs = (raw: any): any[] => {
            if (Array.isArray(raw)) return raw;

            if (typeof raw === 'string') {
                try {
                    return JSON.parse(raw);
                } catch {
                    return [];
                }
            }

            return [];
        };

        // Order-level docs (orderLegId NULL) go under the 'order' filter bucket
        const orderLevelDocs = parseDocs(order?.orderDocuments).map((doc: any) => ({
            ...doc,
            legId: 'order',
            legShowId: order?.orderId ? `#${order.orderId}` : '',
        }));

        const legDocs = (order?.orderLegs ?? []).flatMap((leg: any) => {
            // Match by numeric id — legId is a display string and Leg 1's legId equals the
            // order's orderId, so it can't be used as an identity key.
            const isActiveLeg = currentLegData && String(leg?.id) === String(currentLegData?.id);
            const docs = isActiveLeg ? parseDocs(currentLegData?.documents) : parseDocs(leg?.documents);
            const legShowId = leg?.legId ? `#${leg.legId}` : '';

            return docs.filter((doc: any) => !doc?.isRunResolved).map((doc: any) => ({ ...doc, legId: `${leg.id}`, legShowId }));
        });

        return [...orderLevelDocs, ...legDocs];
    }, [order?.orderLegs, order?.orderDocuments, order?.orderId, currentLegData]);

    // Filter by selected leg — backend handles version deduplication
    const filteredDocs = useMemo(() => {
        const docs = selectedLeg ? allDocs?.filter((doc: any) => String(doc.legId) === String(selectedLeg)) : allDocs;

        // Copy before sort: when no leg is selected we'd otherwise hand the frozen Redux-derived array
        // to .sort() (which mutates in place) and crash with "Cannot assign to read only property '0'".
        return [...(docs ?? [])].sort((a: any, b: any) => new Date(b?.createdAt || 0).getTime() - new Date(a?.createdAt || 0).getTime());
    }, [allDocs, selectedLeg]);

    const isDocNew = (doc: any): boolean => {
        if (doc?.isNew === true) return true;

        const date = doc?.createdAt || doc?.updatedAt;

        if (!date) return false;

        const diff = Date.now() - new Date(date).getTime();

        return diff >= 0 && diff <= NEW_DOC_THRESHOLD_MS;
    };

    const newCount = useMemo(() => filteredDocs.filter(isDocNew).length, [filteredDocs]);

    const headerLeftContent =
        newCount > 0 ? (
            <BadgeCmp style="modern" type="orange" mainClassName="" isHidePillDot={true}>
                <div className="flex items-center gap-1.5">
                    <div className="w-1.5 h-1.5 rounded-full bg-successSecondary"></div>
                    <span>{newCount} New</span>
                </div>
            </BadgeCmp>
        ) : (
            <></>
        );

    const headerRightContent =
        legOptions.length > 1 ? <FilterDropdown options={legOptions} selectedValue={selectedLeg} onSelect={setSelectedLeg} hideOnSingleOption={false} isLoading={loading} /> : undefined;

    // Public share page: the documents the customer chose to share replace the sign-in teaser below.
    const sharedDocuments = (order as any)?.sharedDocuments;

    if (isReadOnly && Array.isArray(sharedDocuments) && sharedDocuments.length > 0) {
        return (
            <CardCmp
                headerTitle={t('ShareRadarOrder.Documents_Title')}
                headerParentClassName="!rounded-t-lg !px-0 !mx-3"
                isCardContentWithoutSpacing={true}
                childrenClassName={SHARED_DOCUMENTS_CONTENT_CLASS}
            >
                <SharedDocumentsList documents={sharedDocuments} kind="order" />
            </CardCmp>
        );
    }

    if (isReadOnly) {
        return (
            <CardCmp
                headerTitle="Documents"
                headerParentClassName="!rounded-t-lg !px-0 !mx-3"
                isCardContentWithoutSpacing={true}
                childrenClassName="widget-card-content-wrap !p-0 max-h-[192px] overflow-hidden custom-scrollbar-v2 p-4"
            >
                <div className="relative">
                    {/* Blurred dummy list */}
                    <ul className="blur-sm pointer-events-none p-4 rounded-b-lg select-none space-y-4" aria-hidden="true">
                        {DUMMY_DOCS.map((doc) => {
                            const fileExtension = doc.fileName.split('.').pop()?.toLowerCase() || '';
                            const fileIcon = getFileExtensionTypeImage(fileExtension);

                            return (
                                <li key={doc.id} className="flex gap-x-2.5 items-center">
                                    <figure className="flex-none">
                                        <img src={fileIcon} alt="icon" className="w-[30px] h-[30px] flex-none object-contain" />
                                    </figure>
                                    <div className="flex-1 overflow-hidden">
                                        <p className="text-xs leading-[1.5] font-medium text-textSecondary truncate">{doc.fileName}</p>
                                        <p className="text-[10px] leading-[14px] font-normal text-gray500">Mon, May 18th, 2026</p>
                                    </div>
                                    <div className="flex items-center gap-x-1.5">
                                        <figure className="bg-utilityBlack flex items-center justify-center w-4 h-4 rounded-full">
                                            <img src={LogoSmall} alt="org logo" className="w-3.5 h-3.5" />
                                        </figure>
                                        <p className="text-xs leading-[18px] font-normal text-gray500">System</p>
                                    </div>
                                </li>
                            );
                        })}
                    </ul>

                    {/* Login overlay */}
                    <div className="absolute inset-0 flex flex-col h-[192px] items-center justify-center gap-3 bg-white/60 backdrop-blur-[2px] z-10 rounded-b-lg">
                        <NotFoundUI
                            icon={<Lock01 className="w-5 h-5 text-primary" />}
                            iconClassName="!bg-primary/10 !border-primary/20 !rounded-full !shadow-none !hidden"
                            title="Login to view documents"
                            desc="Sign in to access and download shipment documents"
                            containerClassName="!my-0 !mx-0"
                            descClass="!pb-0"
                            buttonText="Login to access"
                            buttonType="btn-outline-primary"
                            handleActionType={() => (window.location.href = ROUTES.LOGIN)}
                            isEmptyBackground={true}
                        />
                    </div>
                </div>
            </CardCmp>
        );
    }

    if (loading) {
        return (
            <CardCmp
                headerTitle="Documents"
                headerParentClassName="!rounded-t-lg !px-0 !mx-3"
                isCardContentWithoutSpacing={true}
                childrenClassName="widget-card-content-wrap p-4 max-h-[160px] overflow-auto custom-scrollbar-v2"
                headerRightSideContent={<p className="custom-loading">lorem ipsum</p>}
            >
                <ul className="overflow-auto custom-scrollbar-v2 space-y-4">
                    {[1, 2, 3].map((item) => (
                        <li key={item} className="flex gap-x-2.5 items-center">
                            <figure className="flex-none">
                                <div className="w-[30px] h-[30px] rounded custom-loading" />
                            </figure>
                            <div className="flex-1 overflow-hidden custom-loading">
                                <p className="text-xs leading-[1.5] font-medium text-textSecondary truncate">lorem</p>
                                <p className="text-[10px] leading-[14px] font-normal text-gray500">lorem</p>
                            </div>
                            <div className="flex items-center gap-x-1.5 custom-loading">
                                <div className="w-4 h-4 rounded-full flex-none bg-white" />
                                <p className="text-xs leading-[18px] font-normal text-gray500 truncate max-w-[100px]">lorem</p>
                            </div>
                        </li>
                    ))}
                </ul>
            </CardCmp>
        );
    }

    return (
        <>
            <CardCmp
                headerTitle="Documents"
                headerParentClassName="!rounded-t-lg !px-0 !mx-3"
                isCardContentWithoutSpacing={true}
                childrenClassName={`widget-card-content-wrap ${
                    filteredDocs.length === 0 ? 'min-h-[192px] flex-1 flex items-center justify-center' : 'px-3 py-3 max-h-[160px] overflow-auto custom-scrollbar-v2'
                }`}
                headerLeftSideContent={headerLeftContent}
                headerRightSideContent={headerRightContent}
            >
                {filteredDocs.length === 0 ? (
                    <NotFoundUI
                        title="No Documents found"
                        desc="There are no documents available for this order."
                        containerClassName="!h-auto !my-[0px]"
                        iconClassName="!hidden"
                        titleClassName="!px-0"
                        descClass="!px-0 !max-w-[420px] sm:!max-w-[500px]"
                        iconSecondaryClassName="!hidden"
                        iconSecondary={<File06 className="w-6 h-6 text-primary" />}
                        isEmptyBackground={true}
                    />
                ) : (
                    <ul className="space-y-1">
                        {filteredDocs.map((doc: any, index: number) => {
                            // Carrier uploads (POD / carrier BOL) carry uploadedByCarrier — without this
                            // the widget rendered "-" because uploadedByUser is null for them.
                            const uploadedBy = doc?.uploadedByType === 'carrier' ? doc?.uploadedByCarrier : doc?.uploadedByUser;
                            // Any document with no resolvable uploader name falls back to the WAL/org
                            // identity, same as the Leg Documents table (LegDocumentsRow.tsx). This also
                            // covers carrier uploads whose carrier is masked by carrier visibility —
                            // those used to render a bare "-".
                            const isSystem = doc?.uploadedByType === 'system' || !uploadedBy?.name;
                            const docKey = doc?.id ? String(doc.id) : `doc-${index}`;
                            const fileExtension = (doc?.file || doc?.fileName || '').split('.').pop()?.toLowerCase() || '';
                            const fileIcon = getFileExtensionTypeImage(fileExtension);

                            const isNew = isDocNew(doc);

                            return (
                                <li
                                    key={docKey}
                                    className="flex gap-x-3 items-center cursor-pointer -mx-1.5 px-1.5 py-1.5 rounded-md transition-colors duration-150 hover:bg-utilityGray100"
                                    onClick={() => setPreviewDoc(doc)}
                                >
                                    <figure className="flex-none relative">
                                        <img src={fileIcon} alt="icon" onError={onError} className="w-[30px] h-[30px] flex-none object-contain" />
                                        {isNew && <div className="absolute -top-0.5 -left-0.9 w-2 h-2 rounded-full bg-successSecondary border border-white"></div>}
                                    </figure>
                                    <div className="flex-1 overflow-hidden">
                                        <TruncateTextCmp
                                            message={doc?.fileName || '-'}
                                            refName={{
                                                get current() {
                                                    return documentNameRefs.current[docKey] || null;
                                                },
                                            }}
                                        >
                                            <p
                                                className="text-xs leading-[1.5] font-medium text-textSecondary truncate"
                                                ref={(el) => {
                                                    documentNameRefs.current[docKey] = el;
                                                }}
                                            >
                                                {doc?.fileName || '-'}
                                            </p>
                                        </TruncateTextCmp>
                                        <p
                                            className="text-[10px] leading-[14px] font-normal text-gray500"
                                            dangerouslySetInnerHTML={{
                                                __html: getDateWithSuffixFormat(getFormattedDate(doc?.createdAt || doc?.updatedAt, 'ddd, MMM Do, YYYY hh:mm A', true, true)),
                                            }}
                                        />
                                    </div>
                                    <div className="flex items-center gap-x-1.5 w-[64px] flex-none justify-start">
                                        {isSystem ? (
                                            <>
                                                {orgLogoUrl ? (
                                                    <figure>
                                                        <img
                                                            src={orgLogoUrl}
                                                            alt="org logo"
                                                            onError={onError}
                                                            className={`w-4 h-4 rounded-full border border-utilityBlack flex-none object-contain shadow-sm border-opacity-[0.15] ${
                                                                isWhiteLogo ? 'bg-black' : 'bg-gray50'
                                                            }`}
                                                        />
                                                    </figure>
                                                ) : (
                                                    <figure className="bg-utilityBlack flex items-center justify-center w-4 h-4 rounded-full">
                                                        <img src={LogoSmall} alt="org logo" onError={onError} className="w-3.5 h-3.5" />
                                                    </figure>
                                                )}
                                                <p className="text-xs leading-[18px] font-normal text-gray500">System</p>
                                            </>
                                        ) : (
                                            <>
                                                {uploadedBy?.image && uploadedBy?.imageUrl ? (
                                                    <figure>
                                                        <img
                                                            src={`${uploadedBy.imageUrl}${uploadedBy.image}`}
                                                            alt="avatar"
                                                            onError={onError}
                                                            className="w-4 h-4 rounded-full border border-utilityBlack border-opacity-[0.08] flex-none bg-white object-contain"
                                                        />
                                                    </figure>
                                                ) : (
                                                    <div className="w-4 h-4 rounded-full border border-utilityBlack border-opacity-[0.08] flex-none bg-primary flex items-center justify-center">
                                                        <p className="text-[8px] leading-[12px] font-normal text-white">{getShortName(uploadedBy.name)}</p>
                                                    </div>
                                                )}
                                                <TruncateTextCmp
                                                    message={uploadedBy?.firstName || uploadedBy?.name}
                                                    refName={{
                                                        get current() {
                                                            return uploadedByNameRefs.current[docKey] || null;
                                                        },
                                                    }}
                                                    truncateTextParentClassName="min-w-0 flex-1"
                                                >
                                                    <p
                                                        className="text-xs leading-[18px] font-normal text-gray500 truncate"
                                                        ref={(el) => {
                                                            uploadedByNameRefs.current[docKey] = el;
                                                        }}
                                                    >
                                                        {uploadedBy?.firstName || uploadedBy?.name}
                                                    </p>
                                                </TruncateTextCmp>
                                            </>
                                        )}
                                    </div>
                                </li>
                            );
                        })}
                    </ul>
                )}
            </CardCmp>

            {previewDoc && <DocumentPreviewModal document={previewDoc} handleClose={() => setPreviewDoc(null)} />}
        </>
    );
};

export default DocumentsWidget;
